import { getDefaultExcelFormatForDataType, type WorkbookStateResult } from "../TemplateWorkbookService";
import type { ColumnDefinition, ColumnRow, FilterDefinition, FilterRow } from "../TemplateServices";

const normalizeWorkbookLookupToken = (value: string | null | undefined): string =>
  (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const matchesWorkbookToken = (definition: FilterDefinition, token: string | null | undefined): boolean => {
  const normalizedToken = normalizeWorkbookLookupToken(token);
  if (!normalizedToken) return false;

  return [definition.apiField, definition.key, definition.displayName, definition.displayField]
    .map(normalizeWorkbookLookupToken)
    .includes(normalizedToken);
};

const matchesWorkbookFilter = (definition: FilterDefinition, workbookFilter: WorkbookStateResult["filters"][number]): boolean => {
  const workbookTokens = [workbookFilter.apiField, workbookFilter.displayName, workbookFilter.displayField]
    .map(normalizeWorkbookLookupToken)
    .filter(Boolean);

  const definitionTokens = [definition.apiField, definition.key, definition.displayName, definition.displayField]
    .map(normalizeWorkbookLookupToken)
    .filter(Boolean);

  return workbookTokens.some((token) => definitionTokens.includes(token));
};

export const mapFilterDefinitionToSelectedRow = (definition: FilterDefinition, cascadeFrom: string | null = null): FilterRow => ({
  key: definition.key,
  displayName: definition.displayName,
  apiField: definition.apiField,
  displayField: definition.displayField,
  type: definition.filterControlType,
  required: definition.defaultRequired,
  cascadeFrom,
  cascadeParents: definition.cascadeParents,
  dropdownEndpoint: definition.dropdownEndpoint,
  allowedTypes: definition.allowedTypes,
  generatedFromColumn: Boolean(definition.generatedFromColumn),
  includeInDataCall: definition.includeInDataCall,
  filterValue: "",
});

export const mapColumnDefinitionToSelectedRow = (definition: ColumnDefinition): ColumnRow => ({
  key: definition.key,
  displayName: definition.displayName,
  apiField: definition.apiField,
  dataType: definition.dataType,
  excelFormat: getDefaultExcelFormatForDataType(definition.dataType),
});

export const reorderRows = <TRow>(rows: TRow[], fromIndex: number, toIndex: number): TRow[] => {
  if (fromIndex === toIndex) return rows;
  const next = [...rows];
  const [dragged] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, dragged);
  return next;
};

export const resolveWorkbookColumns = (
  workbookColumns: WorkbookStateResult["columns"],
  apiColumns: ColumnDefinition[]
): ColumnRow[] => {
  return workbookColumns
    .map((workbookColumn): ColumnRow | null => {
      const normalizedApiField = normalizeWorkbookLookupToken(workbookColumn.apiField);
      const apiColumn = apiColumns.find((column) =>
        normalizeWorkbookLookupToken(column.apiField) === normalizedApiField ||
        normalizeWorkbookLookupToken(column.key) === normalizedApiField ||
        normalizeWorkbookLookupToken(column.displayName) === normalizedApiField
      );

      if (!apiColumn) {
        const fallbackKey = workbookColumn.apiField || workbookColumn.displayName;
        if (!fallbackKey) return null;

        return {
          key: fallbackKey,
          displayName: workbookColumn.displayName || fallbackKey,
          apiField: workbookColumn.apiField || fallbackKey,
          dataType: "text",
          excelFormat: workbookColumn.excelFormat || getDefaultExcelFormatForDataType("text"),
        };
      }

      return {
        key: apiColumn.key,
        displayName: workbookColumn.displayName || apiColumn.displayName,
        apiField: apiColumn.apiField,
        dataType: apiColumn.dataType,
        excelFormat: workbookColumn.excelFormat || getDefaultExcelFormatForDataType(apiColumn.dataType),
      };
    })
    .filter((column): column is ColumnRow => column !== null);
};

export const resolveWorkbookFilters = (
  workbookFilters: WorkbookStateResult["filters"],
  allFilterDefinitions: FilterDefinition[]
): FilterRow[] => {
  return workbookFilters
    .map((workbookFilter): FilterRow | null => {
      const definition = allFilterDefinitions.find((filter) => matchesWorkbookFilter(filter, workbookFilter));
      if (!definition) return null;

      let cascadeFrom: string | null = null;
      if (workbookFilter.cascadeFromApiField) {
        const parentDefinition = allFilterDefinitions.find((filter) => matchesWorkbookToken(filter, workbookFilter.cascadeFromApiField));
        cascadeFrom = parentDefinition?.key ?? null;
      }

      return {
        key: definition.key,
        displayName: workbookFilter.displayName || definition.displayName,
        apiField: definition.apiField,
        displayField: definition.displayField,
        type: workbookFilter.type,
        required: workbookFilter.required,
        cascadeFrom,
        cascadeParents: definition.cascadeParents,
        dropdownEndpoint: definition.dropdownEndpoint,
        allowedTypes: definition.allowedTypes,
        generatedFromColumn: Boolean(definition.generatedFromColumn),
        includeInDataCall: definition.includeInDataCall,
        filterValue: workbookFilter.fixedValue || "",
      };
    })
    .filter((filter): filter is FilterRow => filter !== null);
};
